import { ReactComponent as IconAccountSetting } from "@/assets/svgs/account-setting.svg";
import { ReactComponent as IconNotificationSetting } from "@/assets/svgs/notification-setting.svg";
import Tabs, { Tab } from "@/components/Tabs";
import usePage from "@/hooks/use-page/index.js";
import { NavLink, useNavigate, useLocation } from "@/lib/router-dom/index.js";
import { useEffect, useMemo, useState } from "react";
import AccountSetting from "./components/account-setting.jsx";
import { NotificationSetting } from "./components/notification-setting.jsx";
import { PitstopNotificationSetting } from "./components/pitstop-notification-setting.jsx";
import Breadcrumbs from "@/componentsDS/Breadcrumbs/index.jsx";
import ModelTermForm from "./components/model-term-form.jsx";
import useModelTerm from "@/hooks/use-term-template/index.js";
import { useFeature } from "@/hooks/use-feature/index.js";
import { featureConstant } from "@/constants/configuration/feature.js";

const SHELL_HOSTED = process.env.REACT_APP_SHELL_HOSTED === 'true';

const Page = ({ setLoading }) => {
	const { currentPath } = useLocation();
	const { replace } = useNavigate()

	const [createModelTerm, setCreateModelTerm] = useState(false);
	const { fetchModelTermList, modelTermList } = useModelTerm();
	const useCases = sessionStorage.getItem("useCases");
	const isIGPNILegalRequirementsEnabled = useFeature(featureConstant.IG_PNI_LEGAL_REQUIREMENTS)
	// Unified portal: the pitstop notifications tab exists only when this session
	// carries a pitstop token (admin-only users don't get it).
	const hasPitstop = !!sessionStorage.getItem("pitstopCoreToken");

	const pageTitle = "Account Settings";

	useEffect(() => {
		if(currentPath === "/setting"){
			replace("/setting/account")
		}
	}, [])

	useEffect(() => {
		const newTabItems = tabsItems.map((item) => ({
			...item,
			active: item.path === currentPath,
		}));
		setTabItems(newTabItems);
	}, [currentPath]);

	
	const modelTermEnabled = useMemo(() => {
		if(isIGPNILegalRequirementsEnabled && useCases) {
			const parsedUseCases = JSON.parse(useCases)
			return parsedUseCases.some(
				(useCase) =>
					useCase.enableETR && useCase.etrIssuance?.contracts?.length > 0
			);
		}
		return false
	}, [useCases]);

	useEffect(() => {
		if(isIGPNILegalRequirementsEnabled && modelTermEnabled) {
			fetchModelTermList()
		}
	}, [modelTermEnabled])

	const defaultBreadCrumbs = {
		title: pageTitle,
		breadcrumbs: [
			{
				label: '',
				link: `/`,
			},
			{
				label: pageTitle,
				link: ``
			},
		],
	};

	const myAccountTab = {
		title: "My Account",
		label: (
			<NavLink className="setting-btn" to="/setting/account">
				<IconAccountSetting />
				<div className="setting-btn-txt font-weight-bold">My Account</div>
			</NavLink>
		),
		path: "/setting/account",
		active: true,
	};

	const pitstopNotificationTab = {
		title: "Pitstop Notifications",
		label: (
			<NavLink className="setting-btn" to="/setting/pitstop-notification">
				<IconNotificationSetting />
				<div className="setting-btn-txt font-weight-bold">Pitstop Notifications</div>
			</NavLink>
		),
		path: "/setting/pitstop-notification",
		active: false,
	};

	// Legacy label stays "Notifications"; on the unified portal it becomes the
	// admin-scoped tab sitting after the pitstop one.
	const adminNotificationTitle = SHELL_HOSTED ? "Admin Notifications" : "Notifications";
	const adminNotificationTab = {
		title: adminNotificationTitle,
		label: (
			<NavLink className="setting-btn" to="/setting/notification">
				<IconNotificationSetting />
				<div className="setting-btn-txt font-weight-bold">{adminNotificationTitle}</div>
			</NavLink>
		),
		path: "/setting/notification",
		active: false,
	};

	const defaultTabItems = SHELL_HOSTED
		? [myAccountTab, ...(hasPitstop ? [pitstopNotificationTab] : []), adminNotificationTab]
		: [myAccountTab, adminNotificationTab];

	const [tabsItems, setTabItems] = useState(
		defaultTabItems.map((item) => ({
			...item,
			active: item.path === currentPath,
		}))
	);

	const { breadcrumbsItems } = usePage(defaultBreadCrumbs);

	const selectTab = (item) => {
		const newTabItems = tabsItems.map((value) => {
			value.active = item.path === value.path;
			return value;
		});
		setTabItems(newTabItems);
	};

	if (isIGPNILegalRequirementsEnabled && createModelTerm) {
		return (
			<ModelTermForm
				setCreateModelTerm={setCreateModelTerm}
				termTemplate={modelTermList[0]}
			/>
		);
	}

	return (
		<>
			<Breadcrumbs breadcrumbItems={breadcrumbsItems} />

			<div className="title-header">
				<div className="title-header__left">
					<div className="title-header__title font-weight-bold" data-testid="page-title">{pageTitle}</div>
				</div>
			</div>

			<div className="mb-5">
				<Tabs>
					{tabsItems.map((item, index) => (
						<Tab
							key={index}
							label={item.label}
							active={item.active}
							onClick={() => selectTab(item)}
						/>
					))}
				</Tabs>
			</div>
			{currentPath === "/setting/account" && (
				<AccountSetting
					setLoading={setLoading}
					modelTermEnabled={modelTermEnabled}
					setCreateModelTerm={setCreateModelTerm}
					termTemplate={modelTermList[0]}
				/>
			)}

			{currentPath === "/setting/notification" && (
				<NotificationSetting setLoading={setLoading} />
			)}

			{SHELL_HOSTED && hasPitstop && currentPath === "/setting/pitstop-notification" && (
				<PitstopNotificationSetting setLoading={setLoading} />
			)}
		</>
	);
};

export default Page;
